import React, { useState } from 'react';
import { useEducationHub } from '../../../contexts/EducationHubContext';
import { useSearchParams } from 'react-router-dom';
import { Guide, GuideCompletion } from '../../../types/EducationHub';
import styles from '../../../styles/EducationHub.module.css';
import LoadingSpinner from 'components/common/LoadingSpinner';

const GuideSection: React.FC = () => {
  const { guides: initialGuides, completions, guidesLoading: loading } = useEducationHub();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('');
  
  // Get filtered guides
  const filteredGuides = initialGuides.filter(guide => {
    if (selectedCategory && guide.category !== selectedCategory) return false;
    if (selectedDifficulty && guide.difficulty !== selectedDifficulty) return false;
    return true;
  });

  // Get unique categories and difficulties
  const categories = Array.from(new Set(initialGuides.map(g => g.category)));
  const difficulties = ['Beginner', 'Intermediate', 'Advanced'];

  // Get completion for a guide
  const getCompletion = (guideId: string) => {
    return completions.find(c => c.guideId._id === guideId);
  };

  const handleGuideClick = (guide: Guide) => {
    setSearchParams({ type: 'guide', id: guide._id });
  };

  const renderGuideCard = (guide: Guide) => {
    const completion = getCompletion(guide._id);
    const progressPercent = completion?.progress || 0;
    
    return (
      <div
        key={guide._id}
        className={`${styles.contentCard} ${styles.guideCard}`}
        onClick={() => handleGuideClick(guide)}
      >
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>{guide.title}</h3>
          <div className={`${styles.difficultyBadge} ${styles[`difficulty${guide.difficulty}`]}`}>
            {guide.difficulty}
          </div>
        </div>
        
        <p className={styles.cardExcerpt}>{guide.description}</p>
        
        <div className={styles.cardMeta}>
          <div className={styles.cardDate}>
            <span>📂 {guide.category}</span>
            {guide.estimatedDuration && (
              <span>⏱️ {guide.estimatedDuration}min</span>
            )}
          </div>
          
          {completion && (
            <div className={styles.progress}>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span>{progressPercent}%</span>
              {completion.completed && <span>✅</span>}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className={styles.filterContainer}>
        <div className={styles.categoryFilterWrapper}>
          <label className={styles.categoryLabel}>Category:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className={styles.categorySelect}
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div className={styles.categoryFilterWrapper}>
          <label className={styles.categoryLabel}>Difficulty:</label>
          <select
            value={selectedDifficulty}
            onChange={(e) => setSelectedDifficulty(e.target.value)}
            className={styles.categorySelect}
          >
            <option value="">All Levels</option>
            {difficulties.map(difficulty => (
              <option key={difficulty} value={difficulty}>{difficulty}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterResultCount}>
          {filteredGuides.length} guide{filteredGuides.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Guides Grid */}
      {filteredGuides.length > 0 ? (
        <div className={styles.contentGrid}>
          {filteredGuides.map(renderGuideCard)}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📖</div>
          <h3 className={styles.emptyTitle}>
            {initialGuides.length === 0 ? "Welcome to Learning Guides!" : "No Guides Match Your Filters"}
          </h3>
          <p className={styles.emptyDescription}>
            {initialGuides.length === 0 
              ? "Learning guides will appear here once they're published by the platform administrators. Check back soon for new educational content!" 
              : "No guides match your current filters. Try adjusting your search criteria to find what you're looking for."
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default GuideSection;
